// services/seriesProgress.js
// Tracks the user's progress through a TV series — which season they last
// downloaded or played from BrowseMediaView. Surfaced as a "Continue: Season X"
// pill on the show page and the "Continue Watching" rail on /discover + /tv.
//
// CROSS-DEVICE SYNC (the headline fix): progress is server-backed per INVITE,
// not per device — exactly like playback positions (Feature 24). A user who
// downloads/plays a season on their phone sees the same Continue Watching rail
// on their laptop. The previous design stored progress in device-scoped
// localStorage only, so the rail showed on one device and not another for the
// same user (the reported bug).
//
// Architecture (mirrors services/playbackPosition.js):
//   - In-memory Map is the source of truth for synchronous reads
//     (getProgress / listProgress). Hydrated from the server on the first
//     hydrate() call after auth lands.
//   - Writes update the cache synchronously AND fire-and-forget to the
//     server (PUT/DELETE), so reads in the same session feel instant.
//   - A localStorage mirror (scoped by the per-device key) gives the rail
//     something to show before hydrate() returns, without leaking another
//     invite's data on a shared browser.
//
// Separately tracks the "last viewed season" — which season tab the user had
// open on a title's BrowseMediaView. That stays DEVICE-LOCAL (casual browsing
// shouldn't surface a show in everyone's rail, and it isn't worth a round-trip)
// under a different key prefix.

import { useAuthStore } from "@/stores/auth";

const EXPIRY_MS = 365 * 24 * 60 * 60 * 1000; // 1 year — for the local mirror's self-prune
const VIEWED_PREFIX = "putio_series_viewed_";
const VIEWED_EXPIRY_MS = 60 * 24 * 60 * 60 * 1000; // 60 days — staler than play/download progress

// localStorage mirror of the server-backed progress cache. Scoped by the
// per-device key so two device cookies sharing one browser (an admin and an
// invitee on the same TV) can't see each other's pre-hydrate rail. The server
// (per invite) is authoritative; this is just an offline/first-paint cache.
const CACHE_PREFIX = "putio_series_cache_v2_";

// In-memory cache. Keys are the sanitized tmdbId. Values are
// { tmdbId, season, episode, source, updatedAt }.
const cache = new Map();

let hydrated = false;
let hydratePromise = null;

// Pinia may be unavailable during module load / SSR / Vitest setup, in which
// case useAuthStore() throws. Treat that as "no device key yet" — the local
// mirror simply no-ops until auth hydrates, and the server hydrate() fills the
// cache regardless.
function deviceKey() {
  try {
    return useAuthStore().deviceKey || null;
  } catch {
    return null;
  }
}

function sanitizeId(id) {
  if (id == null) return null;
  const s = String(id);
  if (!/^[\w-]+$/.test(s) || s.length > 32) return null;
  return s;
}

function cacheStorageKey() {
  const dk = deviceKey();
  return dk ? `${CACHE_PREFIX}${dk}` : null;
}

function readLocalCache() {
  const k = cacheStorageKey();
  if (!k) return;
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    const now = Date.now();
    for (const entry of parsed) {
      if (!entry || typeof entry !== "object") continue;
      const id = sanitizeId(entry.tmdbId);
      if (!id) continue;
      // Self-prune stale mirror rows so a year-old offline cache can't
      // resurrect a show the server already dropped.
      if (now - new Date(entry.updatedAt).getTime() > EXPIRY_MS) continue;
      cache.set(id, {
        tmdbId: id,
        season: Number(entry.season),
        episode: entry.episode == null ? null : Number(entry.episode),
        source: entry.source || "download",
        updatedAt: entry.updatedAt || new Date().toISOString(),
      });
    }
  } catch {
    // Malformed cache — ignore. Server hydrate() will replace it.
  }
}

function writeLocalCache() {
  const k = cacheStorageKey();
  if (!k) return;
  try {
    localStorage.setItem(k, JSON.stringify(Array.from(cache.values())));
  } catch {
    // Quota / private-mode failures aren't fatal — the server is authoritative.
  }
}

/**
 * Fetch every series-progress row for the calling invite from the server and
 * replace the in-memory cache. Safe to call repeatedly — only the first call
 * hits the wire; later calls return the same in-flight promise. Falls back to
 * the localStorage mirror on a network failure so the rail still shows
 * something useful offline.
 */
export async function hydrate() {
  if (hydrated) return;
  if (hydratePromise) return hydratePromise;

  // Prime from the local mirror first so a synchronous listProgress() right
  // after calling hydrate() (before the await resolves) isn't empty.
  if (cache.size === 0) readLocalCache();

  hydratePromise = (async () => {
    try {
      const res = await fetch("/api/series-progress", {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        // 401 (locked) is expected on public routes — keep the mirror.
        return;
      }
      const body = await res.json();
      const items = Array.isArray(body?.items) ? body.items : [];
      cache.clear();
      for (const row of items) {
        const id = sanitizeId(row?.tmdb_id);
        if (!id) continue;
        cache.set(id, {
          tmdbId: id,
          season: Number(row.season),
          episode: row.episode == null ? null : Number(row.episode),
          source: row.source || "download",
          updatedAt: row.updated_at
            ? new Date(row.updated_at).toISOString()
            : new Date().toISOString(),
        });
      }
      writeLocalCache();
      hydrated = true;
    } catch {
      // Network / DNS failure — keep the local mirror as a best-effort
      // fallback. Retries on the next boot.
    } finally {
      hydratePromise = null;
    }
  })();
  return hydratePromise;
}

/**
 * Record progress on a series. Called when the user adds a torrent to Put.io
 * or instant-plays a torrent for a specific season/episode. Updates the cache
 * synchronously (so the rail reflects it immediately) and PUTs to the server.
 * `season` is required; `episode` is optional (null for season packs).
 * `source` is "download" or "play".
 */
export function setProgress(
  tmdbId,
  { season, episode = null, source = "download" } = {},
) {
  const id = sanitizeId(tmdbId);
  if (!id || season == null) return;
  const seasonNum = Number(season);
  if (!Number.isFinite(seasonNum) || seasonNum < 0) return;
  const episodeNum = episode == null ? null : Number(episode);

  const entry = {
    tmdbId: id,
    season: seasonNum,
    episode: Number.isFinite(episodeNum) ? episodeNum : null,
    source,
    updatedAt: new Date().toISOString(),
  };
  cache.set(id, entry);
  writeLocalCache();

  // Fire-and-forget push to the server.
  try {
    fetch(`/api/series-progress/${encodeURIComponent(id)}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        season: entry.season,
        episode: entry.episode,
        source: entry.source,
      }),
    }).catch(() => {
      /* network error — local cache keeps the value */
    });
  } catch {
    /* total fetch failure — local cache keeps the value */
  }
}

/**
 * Read progress for a series from the cache. Returns null if none. Callers that
 * need the freshest cross-device value should await hydrate() first (App.vue
 * does this on boot; the rail components and BrowseMediaView await it too).
 */
export function getProgress(tmdbId) {
  const id = sanitizeId(tmdbId);
  if (!id) return null;
  return cache.get(id) || null;
}

/**
 * List every saved progress entry for the calling invite, newest first.
 * Powers the "Continue Watching" rail. Synchronous read from the cache —
 * hydrate() must have populated it (the rail awaits hydrate() before reading).
 */
export function listProgress() {
  return Array.from(cache.values()).sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

/**
 * Forget progress for a series — removes it from the rail on every device.
 */
export function clearProgress(tmdbId) {
  const id = sanitizeId(tmdbId);
  if (!id) return;
  cache.delete(id);
  writeLocalCache();
  try {
    fetch(`/api/series-progress/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json" },
    }).catch(() => {
      /* nbd — cache already updated */
    });
  } catch {
    /* nbd */
  }
}

// -- Last-viewed-season tracker (DEVICE-LOCAL) ------------------------------
// Stored separately from setProgress/getProgress so "I clicked the Season 2 tab
// to peek" doesn't end up looking like "I'm actively watching Season 2" on the
// Continue Watching rail. Kept device-local (not synced) — it only defaults the
// season picker and isn't worth a round-trip.

function scopedViewedKey(tmdbId) {
  const dk = deviceKey();
  const safe = sanitizeId(tmdbId);
  if (!dk || !safe) return null;
  return `${VIEWED_PREFIX}${dk}__${safe}`;
}

/**
 * Remember which season tab the user last had open on a title's
 * BrowseMediaView. Called on every season-chip click. Idempotent.
 */
export function setLastViewedSeason(tmdbId, season) {
  const k = scopedViewedKey(tmdbId);
  if (!k || season == null) return;
  const seasonNum = Number(season);
  if (!Number.isFinite(seasonNum) || seasonNum < 0) return;
  try {
    localStorage.setItem(
      k,
      JSON.stringify({
        season: seasonNum,
        updatedAt: new Date().toISOString(),
      }),
    );
  } catch (error) {
    if (
      error instanceof DOMException &&
      (error.name === "QuotaExceededError" || error.code === 22)
    ) {
      cleanExpired();
      try {
        localStorage.setItem(
          k,
          JSON.stringify({
            season: seasonNum,
            updatedAt: new Date().toISOString(),
          }),
        );
      } catch {
        /* give up */
      }
    }
  }
}

/**
 * Read the last-viewed season for a title. Returns null if none / expired.
 * Used by BrowseMediaView to default the season picker to wherever the user
 * last left off.
 */
export function getLastViewedSeason(tmdbId) {
  const k = scopedViewedKey(tmdbId);
  if (!k) return null;
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    const age = Date.now() - new Date(entry.updatedAt).getTime();
    if (age > VIEWED_EXPIRY_MS) {
      localStorage.removeItem(k);
      return null;
    }
    return Number.isFinite(entry.season) ? entry.season : null;
  } catch {
    return null;
  }
}

/**
 * Drop expired last-viewed entries belonging to the current device, AND every
 * legacy un-scoped entry under the viewed prefix regardless of age (the
 * cross-user leak source from before deviceKey scoping). Progress retention is
 * owned by the server now, so this only services the device-local viewed store.
 */
export function cleanExpired() {
  const dk = deviceKey();
  const scopedViewedPrefix = dk ? `${VIEWED_PREFIX}${dk}__` : null;
  const now = Date.now();
  const drop = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(VIEWED_PREFIX)) continue;
      const rest = k.slice(VIEWED_PREFIX.length);
      const isLegacyUnscoped = !rest.includes("__");
      if (isLegacyUnscoped) {
        drop.push(k);
        continue;
      }
      const isCurrentDeviceScoped =
        scopedViewedPrefix && k.startsWith(scopedViewedPrefix);
      if (!isCurrentDeviceScoped) continue;
      const raw = localStorage.getItem(k);
      if (!raw) {
        drop.push(k);
        continue;
      }
      try {
        const entry = JSON.parse(raw);
        if (now - new Date(entry.updatedAt).getTime() > VIEWED_EXPIRY_MS)
          drop.push(k);
      } catch {
        drop.push(k);
      }
    }
    for (const k of drop) localStorage.removeItem(k);
  } catch {
    /* noop */
  }
}

// Test hook: reset the in-memory cache + hydrate latch between tests.
export function __resetForTest() {
  cache.clear();
  hydrated = false;
  hydratePromise = null;
}
